import { useTranslation } from 'react-i18next'

import {
  Box,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import type {
  AreaDef,
  KomaRow,
  PriceSeriesCode,
} from '~/pages/SpotPrice/constants'
import { AREAS, WEATHER_MOCK, komaPrice } from '~/pages/SpotPrice/constants'

// ── 集約計算ヘルパー ──
// koma 番号は 1-based inclusive。 複数日は全日の行を連結した上で同じ範囲で平均する
// (design と同じ)。 価格が無い (null) 行は計算に入れない。
function pricesOf(
  rows: KomaRow[],
  code: PriceSeriesCode,
  ranges?: number[][],
): number[] {
  const out: number[] = []
  for (const pt of rows) {
    const price = komaPrice(pt, code)
    if (price === null) continue
    if (ranges && !ranges.some(([lo, hi]) => pt.koma >= lo && pt.koma <= hi)) {
      continue
    }
    out.push(price)
  }
  return out
}

function avgRange(
  rows: KomaRow[],
  code: PriceSeriesCode,
  ranges: number[][],
): number | null {
  const prices = pricesOf(rows, code, ranges)
  return prices.length > 0
    ? prices.reduce((sum, p) => sum + p, 0) / prices.length
    : null
}

function maxAll(rows: KomaRow[], code: PriceSeriesCode): number | null {
  const prices = pricesOf(rows, code)
  return prices.length > 0 ? Math.max(...prices) : null
}

function minAll(rows: KomaRow[], code: PriceSeriesCode): number | null {
  const prices = pricesOf(rows, code)
  return prices.length > 0 ? Math.min(...prices) : null
}

function fmt(v: number | null): string {
  return v !== null ? v.toFixed(2) : '—'
}

type StatRowDef = {
  label: string
  subLabel?: string
  fn: (code: PriceSeriesCode) => number | null
  spreadFn: () => string
}

/** 東京 − 関西 (東西スプレッド)。 どちらかの値が無ければ「—」。 */
function spreadOf(stat: (code: PriceSeriesCode) => number | null): string {
  const t = stat('tokyo')
  const k = stat('kansai')
  return t !== null && k !== null ? (t - k).toFixed(2) : '—'
}

/** 時間帯 (コマ範囲) の平均の行。 */
function avgRow(
  rows: KomaRow[],
  label: string,
  ranges: number[][],
): StatRowDef {
  const fn = (c: PriceSeriesCode): number | null => avgRange(rows, c, ranges)
  return { label, fn, spreadFn: () => spreadOf(fn) }
}

function buildStatRows(rows: KomaRow[]): StatRowDef[] {
  return [
    avgRow(rows, 'Base (24h)', [[1, 48]]),
    avgRow(rows, '8-20 (Peak)', [[17, 40]]),
    avgRow(rows, '8-22', [[17, 44]]),
    avgRow(rows, '10-17', [[21, 34]]),
    avgRow(rows, '20-8 (Off-Peak)', [
      [1, 16],
      [41, 48],
    ]),
    avgRow(rows, '22-8', [
      [1, 16],
      [45, 48],
    ]),
    {
      label: 'Max',
      fn: (c) => maxAll(rows, c),
      spreadFn: () => spreadOf((c) => maxAll(rows, c)),
    },
    {
      label: 'Min',
      fn: (c) => minAll(rows, c),
      spreadFn: () => spreadOf((c) => minAll(rows, c)),
    },
  ]
}

type Props = {
  komaRows: KomaRow[]
  visibleAreas: Set<string>
  isMultiDay: boolean
}

export const StatsTable = ({
  komaRows,
  visibleAreas,
  isMultiDay,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const visibleAreaDefs = AREAS.filter((a: AreaDef) => visibleAreas.has(a.code))
  const showSpread = visibleAreas.has('tokyo') && visibleAreas.has('kansai')

  const statRows = buildStatRows(komaRows)

  const borderColor = LightModeSemanticColors.Border.lowEmphasis.value
  const surfaceSecondary = LightModeSemanticColors.Surface.secondary.value
  const textHigh = LightModeSemanticColors.Text.highEmphasis.value
  const textMid = LightModeSemanticColors.Text.midEmphasis.value

  const thStyle: React.CSSProperties = {
    background: TABLE_HEADER_BG,
    padding: '6px 10px',
    border: `1px solid ${borderColor}`,
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 14,
    color: textHigh,
    whiteSpace: 'nowrap',
  }
  const tdLabelStyle: React.CSSProperties = {
    padding: '6px 10px',
    border: `1px solid ${borderColor}`,
    textAlign: 'left',
    fontWeight: 600,
    background: surfaceSecondary,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    color: textHigh,
    fontSize: 14,
  }
  const tdStyle: React.CSSProperties = {
    padding: '6px 10px',
    border: `1px solid ${borderColor}`,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
    fontSize: 14,
    color: textHigh,
  }
  const tdAltStyle: React.CSSProperties = {
    ...tdStyle,
    background: surfaceSecondary,
  }

  return (
    <Box
      // 行 hover は 8080 `.sm-stats-table tbody tr:hover` と同じ Hover.lowEmphasis
      // (UI 辞書 `components/table.md` §0)。 地を持つセル (行見出し・偶数行) は
      // 8080 と同じくセルの地が勝つ。
      overflowX="auto"
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      css={{ '& tbody tr:hover': { background: 'Hover.lowEmphasis' } }}
    >
      {/*
       * [mock差異] design は table-layout:fixed + col% だが、 狭幅で nowrap な
       * 行見出し / システムプライス / 東西スプレッド が固定%列をはみ出してセル
       * 途中で見切れる (= 右端の白見切れ)。 table-layout:auto にして列を内容幅で
       * 確保し、 幅に余裕があれば 100% へ伸長・足りなければ wrapper の overflow-x
       * で列単位にクリーンに横スクロールさせる (= セル途中見切れを解消)。
       */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 14,
          tableLayout: 'auto',
        }}
      >
        <thead>
          <tr>
            {/* 外周 border を抜く (wrap の 1px border と二重化解消) */}
            <th style={{ ...thStyle, borderLeft: 0, borderTop: 0 }}>
              {/* row label header — empty per design */}
            </th>
            {visibleAreaDefs.map((a) => (
              <th key={a.code} style={{ ...thStyle, borderTop: 0 }}>
                {a.name}
              </th>
            ))}
            {showSpread && (
              <th style={{ ...thStyle, borderTop: 0, borderRight: 0 }}>
                {t('page:spotPrice.stats.eastWestSpread')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {statRows.map((r, ri) => {
            const isEven = (ri + 1) % 2 === 0
            const rowTd: React.CSSProperties = isEven
              ? { ...tdStyle, background: surfaceSecondary }
              : tdStyle
            const isLast = ri === statRows.length - 1
            const bBottom = isLast ? 0 : undefined
            return (
              <tr key={r.label}>
                <td
                  style={{
                    ...tdLabelStyle,
                    borderLeft: 0,
                    borderBottom: bBottom,
                  }}
                >
                  {r.label}
                </td>
                {visibleAreaDefs.map((a) => (
                  <td key={a.code} style={{ ...rowTd, borderBottom: bBottom }}>
                    {fmt(r.fn(a.code))}
                  </td>
                ))}
                {showSpread && (
                  <td
                    style={{ ...rowTd, borderRight: 0, borderBottom: bBottom }}
                  >
                    {r.spreadFn()}
                  </td>
                )}
              </tr>
            )
          })}

          {/* 天気予報行 */}
          <WeatherRow
            isMultiDay={isMultiDay}
            label={
              <>
                {t('page:spotPrice.stats.weather')}
                {/* 8080 `.sm-stats-sublabel` = font-size xs (10px) / 400 / mid */}
                <span
                  style={{
                    display: 'block',
                    fontSize: fontSizes.xs.value,
                    fontWeight: 400,
                    color: textMid,
                  }}
                >
                  {t('page:spotPrice.stats.weatherNote')}
                </span>
              </>
            }
            rowIndex={8}
            showSpread={showSpread}
            tdAltStyle={tdAltStyle}
            tdLabelStyle={tdLabelStyle}
            tdStyle={tdStyle}
            valueFor={(a) => (isMultiDay ? '—' : (WEATHER_MOCK[a.code] ?? '—'))}
            visibleAreaDefs={visibleAreaDefs}
          />

          {/* 最高気温行 */}
          <WeatherRow
            isMultiDay={isMultiDay}
            label={t('page:spotPrice.stats.maxTemp')}
            rowIndex={9}
            showSpread={showSpread}
            tdAltStyle={tdAltStyle}
            tdLabelStyle={tdLabelStyle}
            tdStyle={tdStyle}
            valueFor={() => (isMultiDay ? '—' : '15.0')}
            visibleAreaDefs={visibleAreaDefs}
          />

          {/* 最低気温行 */}
          <WeatherRow
            isLast
            isMultiDay={isMultiDay}
            label={t('page:spotPrice.stats.minTemp')}
            rowIndex={10}
            showSpread={showSpread}
            tdAltStyle={tdAltStyle}
            tdLabelStyle={tdLabelStyle}
            tdStyle={tdStyle}
            valueFor={() => (isMultiDay ? '—' : '8.0')}
            visibleAreaDefs={visibleAreaDefs}
          />
        </tbody>
      </table>
    </Box>
  )
}

type WeatherRowProps = {
  label: React.ReactNode
  rowIndex: number
  isLast?: boolean
  visibleAreaDefs: AreaDef[]
  showSpread: boolean
  isMultiDay: boolean
  valueFor: (a: AreaDef) => string
  tdLabelStyle: React.CSSProperties
  tdStyle: React.CSSProperties
  tdAltStyle: React.CSSProperties
}

const WeatherRow = ({
  label,
  rowIndex,
  isLast = false,
  visibleAreaDefs,
  showSpread,
  valueFor,
  tdLabelStyle,
  tdStyle,
  tdAltStyle,
}: WeatherRowProps): React.ReactElement => {
  const isEven = (rowIndex + 1) % 2 === 0
  const rowTd = isEven ? tdAltStyle : tdStyle
  const bBottom = isLast ? 0 : undefined
  return (
    <tr>
      <td style={{ ...tdLabelStyle, borderLeft: 0, borderBottom: bBottom }}>
        {label}
      </td>
      {visibleAreaDefs.map((a) => (
        <td key={a.code} style={{ ...rowTd, borderBottom: bBottom }}>
          {valueFor(a)}
        </td>
      ))}
      {showSpread && (
        <td style={{ ...rowTd, borderRight: 0, borderBottom: bBottom }} />
      )}
    </tr>
  )
}
